import { Flex, FlexProps } from '@chakra-ui/react'
import { InputSize } from '@hb/shared'
import { forwardRef, PropsWithChildren } from 'react'
import { InputProps } from '../../../../types/fields'
import { useStatusBorderColor } from './hooks'
import { getInputSizeTokens, useInputSize } from './sizes'

export type InputContainerProps = {
  hideLabel?: boolean
  flexProps?: FlexProps
} & InputProps<any>

// Horizontal / vertical padding come from the size tokens — spec Section 0.6.5 "Input fields"
// (10px 6px at `md`).
export const getInputContainerPaddingX = (size: InputSize | undefined) =>
  getInputSizeTokens(size).containerPaddingX
export const getInputContainerPaddingY = (size: InputSize | undefined) =>
  getInputSizeTokens(size).containerPaddingY

export const getInputContainerPadding = (size: InputSize | undefined) => ({
  px: getInputContainerPaddingX(size),
  py: getInputContainerPaddingY(size),
})

const InputContainer = forwardRef<HTMLDivElement, PropsWithChildren<InputContainerProps>>(
  ({ children, field, meta, flexProps, style, input, size: sizeProp }, ref) => {
    const { name } = input
    const size = useInputSize(sizeProp ?? field?.size)
    const borderColor = useStatusBorderColor(field, meta)

    return (
      <Flex
        // 8px radius + 0.5px border that turns sage green on focus — spec Section 0.6.5.
        borderRadius="8px"
        border="0.5px solid"
        borderColor={borderColor}
        transition="border-color 0.15s ease"
        align="center"
        pos="relative"
        ref={ref}
        {...getInputContainerPadding(size)}
        bg="white"
        _dark={{ bg: 'gray.900' }}
        data-child-name={name}
        {...flexProps}
        style={{ ...flexProps?.style, ...style }}>
        {children}
      </Flex>
    )
  },
)

export default InputContainer
